"use client"
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useDispatch, useSelector } from 'react-redux'
import { Box, Text, Icon } from '@quintoandar-tokko/jopijs'
import ImageNext from "@/components/shared/ImageNext"
import { setCalendarData } from '@/store/slices/components/calendar/CalendarSlice'
import CalendarButton from '../../calendarButton/CalendarButton'
import { formatDate, formatHour } from '../../utils/dates';
import getVisitStatus from '../../utils/getVisitStatus';
import Trans from '@/components/shared/Trans'
import translations from '../visitInformation.trans.json'
import style from '../visitInformation.module.scss'

const CompanyThumbnail = () => {
  const { company } = useSelector(state => state.CalendarSlice)

  if (!company.thumbnail) return

  return (
    <Box className={style.visitInformationCompanyThumbnailWrapper}>
      <ImageNext
        className={style.visitInformationCompanyThumbnail}  
        src={company.thumbnail}
        width="124"
        height="40"
        alt="company"
        unoptimized
      />
    </Box>
  )
}

export const VisitInformationHeader = () => {
  const pathname = usePathname()
  const isCalendarPage = pathname.includes('/calendar/')
  
  if (isCalendarPage) return null
  
  return <CompanyThumbnail />
}

export const VisitInformationTitle = () => {
  const pathname = usePathname()
  const { visit } = useSelector(state => state.CalendarSlice)
  const isVisitPage = pathname.includes('/visit/')
  const isCalendarPage = pathname.includes('/calendar/')
  const visitStatus = getVisitStatus(visit)
  
  return (
    <>
      <Text className={style.visitInformationTitle}>
        {isCalendarPage && Trans(translations, 'PropertyVisit')}
        {isVisitPage && visitStatus.isConfirmed && Trans(translations, 'VisitConfirmed')}
        {isVisitPage && visitStatus.isCanceled && Trans(translations, 'CanceledVisit')}
        {isVisitPage && visitStatus.isPast && Trans(translations, 'FinishedVisit')}
        {isVisitPage && visitStatus.isFinished && Trans(translations, 'FinishedVisit')}
        {isVisitPage && visitStatus.isPending && Trans(translations, 'RequestSent')}
        {isVisitPage && visitStatus.isRejected && Trans(translations, 'RejectedVisit')}
      </Text>
    </>
  )
}

export const VisitInformationPropertyCard = () => {
  const { property } = useSelector(state => state.CalendarSlice)

  return (
    <Box className={style.visitInformationPropertyCard}>
      <Box className={style.visitInformationPropertyThumbnailWrapper}>
        {property.thumbnail
          ? <ImageNext
            className={style.visitInformationPropertyThumbnail}
            src={property.thumbnail}
            width="100"
            height="60"
            alt="property"
            unoptimized
          />
          : <Icon icon="icon-propiedades" fontSize="24px" color="var(--colors-neutral-grey-800)" />
        }
      </Box>
  
      <Box className={style.visitInformationCardInformation}>
        <Text className={style.visitInformationPropertyName}>
          {property.name}
        </Text>
        <Text className={style.visitInformationPropertyLocationText}>
          {property.location}
        </Text>
      </Box>
    </Box>
  )
}

export const VisitInformationVisitDetails = () => {
  const pathname = usePathname()
  const { visit, step, participants } = useSelector(state => state.CalendarSlice)
  const isVisitPage = pathname.includes('/visit/')
  const isCalendarPage = pathname.includes('/calendar/')
  const visitStatus = getVisitStatus(visit)

  const shouldShowInformationDate = (isCalendarPage && step > 1) || isVisitPage

  return (
    <>
      {shouldShowInformationDate && (
        <Box className={style.visitInformationIconRow} data-strikethrough={visitStatus.isCanceled}>
          <Box className={style.visitInformationIconWrapper}>
            <Icon icon="icon-calendario" fontSize="16px" color="var(--colors-neutral-grey-800)"/>
          </Box>
          <Text className={style.visitInformationIconText}>
            {`${formatDate(visit.date)}, ${formatHour(visit.hour)}`}
          </Text>
        </Box>
      )}

      {visit.visitDurationInMinutes ? (
        <Box className={style.visitInformationIconRow}>
          <Box className={style.visitInformationIconWrapper}>
            <Icon icon="icon-reloj" fontSize="16px" color="var(--colors-neutral-grey-800)" />
          </Box>
          <Text className={`${style.visitInformationIconText} ${style.visitInformationIconTextBold}`}>
            {visit.visitDurationInMinutes} min 
          </Text>
        </Box>
      ) : null}


      {participants.length > 0 && (
        <Box className={style.visitInformationIconRow}>
          <Box className={style.visitInformationIconWrapper}>
            <Icon icon="icon-contactos" fontSize="16px" color="var(--colors-neutral-grey-800)"/>
          </Box>
          <Text className={style.visitInformationIconText}>
            {Trans(translations, "With")}
            {' '}
            {participants
              .map(participant => participant.fullname)
              .join(', ')
            }
          </Text>
        </Box>
      )}

      {isVisitPage && visitStatus.isPending && (
        <Box className={style.visitInformationRequestContainer}>
          <Text className={style.visitInformationRequestTitle}>
            {Trans(translations, 'VisitInformation.RequestInfoTitle')}
          </Text>
          <Text className={style.visitInformationRequestDescription}>
            {Trans(translations, 'VisitInformation.RequestInfoDescription')}
          </Text>
        </Box>
      )}
    </>
  )
}

export const VisitInformationFooter = () => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const dispatch = useDispatch()
  const contact_id = searchParams.get('contact_id')
  const { visit, property } = useSelector(state => state.CalendarSlice)
  const isVisitPage = pathname.includes('/visit/')
  const isCalendarPage = pathname.includes('/calendar/')
  const googleCalendarUrl = visit.googleCalendarUrl
  const outlookCalendarUrl = visit.outlookCalendarUrl
  const visitStatus = getVisitStatus(visit)

  const handleIntentionCancelVisit = () => {
    dispatch(setCalendarData({ key: 'visit', subkey: 'cancelVisitModalOpen', value: true }))
  }
  
  const handleClickBookAnotherVisit = () => {
    const propertyId = property.id
    const contactId = contact_id
    let url = `/calendar/${propertyId}`

    if (contactId) {
      url += `?contact_id=${contactId}`
    }
    router.push(url)
  }

  return (
    <Box
      as="footer"
      className={style.visitInformationFooter}
    >
      {isCalendarPage && <CompanyThumbnail />}

      {isVisitPage && (!visitStatus.isConfirmed && !visitStatus.isPending) && (
        <CalendarButton
          variant='subtle'
          onClick={handleClickBookAnotherVisit}
          __css={{ width: '100%' }}
        >
          {Trans(translations, 'BookAnotherVisit')}
        </CalendarButton>
      )}

      {isVisitPage && visitStatus.isPending && (
        <CalendarButton
          variant='subtle'
          onClick={handleIntentionCancelVisit}
          __css={{ width: '100%' }}
        >
          {Trans(translations, 'CancelVisitRequest')}
        </CalendarButton>
      )}

      {isVisitPage && visitStatus.isConfirmed && (
        <>
          <CalendarButton
            icon='google'
            href={googleCalendarUrl}
            target="_blank"
            disabled={!googleCalendarUrl}
            variant='secondary'
          >
            {Trans(translations, 'AddToGoogleCalendar')}
          </CalendarButton>

          <CalendarButton
            icon='outlook'
            href={outlookCalendarUrl}
            target="_blank"
            disabled={!outlookCalendarUrl}
            variant='secondary'
          >
            {Trans(translations, 'AddToOutlookCalendar')}
          </CalendarButton>

          <CalendarButton
            variant='subtle'
            onClick={handleIntentionCancelVisit}
          >
            {Trans(translations, 'CancelVisit')}
          </CalendarButton>
        </>
      )}
    </Box>
  )
}